/**
 * BRAND PRODUCT MODAL
 * 
 * Modal for brand owners to add and edit products in their catalog.
 * Simplified version of the user's AddPolishModal - focused on official product info.
 * 
 * Features:
 * - Product name and description
 * - Color description
 * - Category (Nail Lacquer, Top Coat, etc.)
 * - Photo upload
 * - Collection assignment
 * - Status (available, sold out, coming soon, discontinued)
 * - Price (optional)
 */

import { useState, useRef, useEffect } from 'react';
import { X, Upload, DollarSign, Tag, Camera, AlertTriangle } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { normalizeTypography } from '../../utils/normalizeTypography';
import { db, storage } from '../../firebase/config';
import type { BrandCollection } from '../../types/brand';
import { compressImage, ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';

// Brand storage limit (500 MB in bytes)
const BRAND_STORAGE_LIMIT = 500 * 1024 * 1024; // 500 MB
const BRAND_STORAGE_WARNING = 400 * 1024 * 1024; // 400 MB - warn at 80%

// Product status options
type ProductStatus = 'available' | 'coming_soon' | 'sold_out' | 'discontinued';

const STATUS_OPTIONS: { value: ProductStatus; label: string; emoji: string; color: string }[] = [
  { value: 'available', label: 'Available', emoji: '✅', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'coming_soon', label: 'Coming Soon', emoji: '🔜', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'sold_out', label: 'Sold Out', emoji: '🚫', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'discontinued', label: 'Discontinued', emoji: '⏹️', color: 'bg-gray-100 text-gray-700 border-gray-300' },
];

// Category options (same as user collection)
const CATEGORY_OPTIONS = [
  'Nail Lacquer',
  'Gel Polish',
  'Dip Powder',
  'Top Coat',
  'Base Coat',
  'Treatment',
  'Nail Art',
  'Press-Ons',
  'Other',
];

// Photo with credit information
interface ProductPhoto {
  url: string;
  caption?: string;
  creditName?: string;
  creditUrl?: string;
}

// Product interface
interface BrandProduct {
  id?: string;
  name: string;
  // What type of product this is. Optional for backwards compat —
  // pre-launch brand-catalog records didn't set it and read as
  // undefined; the app defaults them to 'polish' at render time.
  itemType?: import('../../types').ItemType;
  description?: string | null;
  colorDescription?: string | null;
  category: string;
  photos: ProductPhoto[];
  collectionId?: string | null;
  collectionName?: string | null;
  status: ProductStatus;
  price?: number | null;
  releaseDate?: string | null;
  sku?: string | null;
  tags?: string[];
  createdAt?: any;
  updatedAt?: any;
}

interface BrandProductModalProps {
  brandId: string;
  brandName: string;
  collections: BrandCollection[];
  product?: BrandProduct | null; // For editing existing product
  onClose: () => void;
  onSave: (product: BrandProduct) => void;
}

export default function BrandProductModal({
  brandId,
  brandName,
  collections,
  product: existingProduct,
  onClose,
  onSave
}: BrandProductModalProps) {
  const isEditing = !!existingProduct;
  
  // Basic fields
  const [name, setName] = useState(existingProduct?.name || '');
  const [description, setDescription] = useState(existingProduct?.description || '');
  const [colorDescription, setColorDescription] = useState(existingProduct?.colorDescription || '');
  const [category, setCategory] = useState(existingProduct?.category || 'Nail Lacquer');
  // itemType — new field; existing records fall back to 'polish' since the
  // brand-catalog was polish-only until this multi-type feature.
  const [itemType, setItemType] = useState<import('../../types').ItemType>(
    existingProduct?.itemType || 'polish'
  );
  
  // Photos - convert legacy string[] to ProductPhoto[] if needed
  const initializePhotos = (): ProductPhoto[] => {
    if (!existingProduct?.photos) return [];
    return existingProduct.photos.map(photo => {
      if (typeof photo === 'string') {
        // Legacy format - just a URL string
        return { url: photo };
      }
      // Already in new format
      return photo as ProductPhoto;
    });
  };
  
  const [photos, setPhotos] = useState<ProductPhoto[]>(initializePhotos());
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Collection assignment
  const [collectionId, setCollectionId] = useState(existingProduct?.collectionId || '');
  
  // Status and pricing
  const [status, setStatus] = useState<ProductStatus>(existingProduct?.status || 'available');
  const [price, setPrice] = useState(existingProduct?.price?.toString() || '');
  const [sku, setSku] = useState(existingProduct?.sku || '');
  
  // Tags
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(existingProduct?.tags || []);
  
  // Submission state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Storage tracking
  const [brandStorageUsed, setBrandStorageUsed] = useState<number>(0);
  const [bytesUploadedThisSession, setBytesUploadedThisSession] = useState<number>(0);
  
  // Fetch current brand storage on mount
  useEffect(() => {
    const fetchBrandStorage = async () => {
      try {
        const brandDoc = await getDoc(doc(db, 'brands', brandId));
        if (brandDoc.exists()) {
          setBrandStorageUsed(brandDoc.data().storageUsed || 0);
        }
      } catch (err) {
        console.error('Error fetching brand storage:', err);
      }
    };
    fetchBrandStorage();
  }, [brandId]);
  
  // Calculate total storage (existing + uploaded this session)
  const totalStorage = brandStorageUsed + bytesUploadedThisSession;
  const isOverLimit = totalStorage > BRAND_STORAGE_LIMIT;
  const isNearLimit = totalStorage > BRAND_STORAGE_WARNING;
  
  // File input ref
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Handle photo upload with compression and storage tracking
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      let totalBytesUploaded = 0;

      const uploadPromises = Array.from(files).map(async (file): Promise<ProductPhoto> => {
        // Compress the image before uploading (handles HEIC conversion too)
        const compressedFile = await compressImage(file);

        // Track the compressed file size
        totalBytesUploaded += compressedFile.size;

        const storageRef = ref(storage, `brands/${brandId}/products/${Date.now()}-${compressedFile.name}`);
        await uploadBytes(storageRef, compressedFile, {
          cacheControl: 'public, max-age=31536000, immutable',
          contentType: 'image/jpeg',
        });
        const url = await getDownloadURL(storageRef);
        return {
          url,
          caption: '',
          creditName: '',
          creditUrl: '',
        };
      });

      const newPhotos = await Promise.all(uploadPromises);
      setPhotos(prev => [...prev, ...newPhotos]);

      // Update local tracking
      setBytesUploadedThisSession(prev => prev + totalBytesUploaded);

      // Update brand's storageUsed in Firestore
      await updateDoc(doc(db, 'brands', brandId), {
        storageUsed: increment(totalBytesUploaded)
      });

    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload photos');
    } finally {
      setUploadingPhoto(false);
      // Reset input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  // Update photo credit/caption
  const updatePhotoField = (index: number, field: keyof ProductPhoto, value: string) => {
    setPhotos(prev => prev.map((photo, i) => 
      i === index ? { ...photo, [field]: value } : photo
    ));
  };

  // Remove a photo
  const handleRemovePhoto = async (index: number) => {
    const photoToRemove = photos[index];

    // Only delete from Firebase Storage if it's our hosted photo
    const isFirebasePhoto = photoToRemove.url.includes('firebasestorage.app') ||
                            photoToRemove.url.includes('googleapis.com/storage');

    if (isFirebasePhoto) {
      try {
        // Get file size before deleting
        const url = new URL(photoToRemove.url);
        const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
        if (pathMatch) {
          const storagePath = decodeURIComponent(pathMatch[1]);
          const photoRef = ref(storage, storagePath);
          const metadata = await getMetadata(photoRef);
          const fileSize = metadata.size;

          // Delete the file
          await deleteObject(photoRef);

          // Subtract from brand storage count
          await updateDoc(doc(db, 'brands', brandId), {
            storageUsed: increment(-fileSize)
          });

          // Update local tracking
          setBrandStorageUsed(prev => Math.max(0, prev - fileSize));
        }
      } catch (err) {
        console.error('Error deleting photo from storage:', err);
        // Continue anyway - still remove from the list
      }
    }

    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Add a tag
  const handleAddTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags(prev => [...prev, trimmed]);
      setTagInput('');
    }
  };

  // Remove a tag
  const handleRemoveTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Product name is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Find collection name if assigned
      const selectedCollection = collections.find(c => c.id === collectionId);
      
      // Build product data. Free-text fields go through normalizeTypography
      // so smart quotes/dashes (auto-corrected by iOS) don't create
      // typographic duplicates with keyboard-typed versions elsewhere.
      const productData: Partial<BrandProduct> = {
        name: normalizeTypography(name.trim()),
        itemType,
        description: normalizeTypography(description.trim()) || null,
        colorDescription: normalizeTypography(colorDescription.trim()) || null,
        category,
        photos,
        collectionId: collectionId || null,
        collectionName: selectedCollection?.name || null,
        status,
        price: price ? parseFloat(price) : null,
        sku: sku.trim() || null,
        tags: tags.length > 0 ? tags : [],
        updatedAt: serverTimestamp(),
      };
      
      if (isEditing && existingProduct?.id) {
        // Update existing product
        await updateDoc(
          doc(db, 'brands', brandId, 'items', existingProduct.id),
          productData
        );
        
        onSave({ ...existingProduct, ...productData, id: existingProduct.id } as BrandProduct);
      } else {
        // Create new product
        const newProductData = {
          ...productData,
          brandId,
          brandName,
          createdAt: serverTimestamp(),
        };
        
        const docRef = await addDoc(
          collection(db, 'brands', brandId, 'items'),
          newProductData
        );
        
        onSave({ ...newProductData, id: docRef.id } as BrandProduct);
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Product' : 'Add Product'}
            </h2>
            <p className="text-sm text-gray-500">{brandName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Storage Warning Banner */}
          {(isOverLimit || isNearLimit) && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${
              isOverLimit 
                ? 'bg-red-50 border border-red-200 text-red-700' 
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">
                  {isOverLimit ? 'Storage limit exceeded' : 'Approaching storage limit'}
                </p>
                <p>
                  Using {(totalStorage / (1024 * 1024)).toFixed(1)} MB of 500 MB.
                  {isOverLimit 
                    ? ' Please contact support to upgrade your brand storage.' 
                    : ' Consider optimizing images or upgrading soon.'}
                </p>
              </div>
            </div>
          )}

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Photos
            </label>
            
            {/* Photo credit reminder */}
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                <span><strong>Swatcher credit matters!</strong> If using a community member's swatch photo, please credit them.</span>
              </p>
            </div>
            
            {/* Photo list with credit fields */}
            <div className="space-y-4 mb-3">
              {photos.map((photo, index) => (
                <div key={index} className="flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Photo thumbnail */}
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <img
                      src={photo.url}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-purple-600 text-white text-xs rounded">
                        Main
                      </span>
                    )}
                  </div>
                  
                  {/* Photo details */}
                  <div className="flex-1 space-y-2">
                    {/* Caption */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Caption <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={photo.caption || ''}
                        onChange={(e) => updatePhotoField(index, 'caption', e.target.value)}
                        placeholder="e.g., 2 coats in direct sunlight"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    
                    {/* Credit fields */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          📷 Photo Credit <span className="text-gray-400">(swatcher name)</span>
                        </label>
                        <input
                          type="text"
                          value={photo.creditName || ''}
                          onChange={(e) => updatePhotoField(index, 'creditName', e.target.value)}
                          placeholder="e.g., @nailsbyjane"
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Credit Link <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="url"
                          value={photo.creditUrl || ''}
                          onChange={(e) => updatePhotoField(index, 'creditUrl', e.target.value)}
                          placeholder="Instagram or profile URL"
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Upload button */}
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="w-full py-6 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-purple-400 hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                {uploadingPhoto ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent"></div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload photos</span>
                    <span className="text-xs text-gray-400 mt-1">Auto-compressed to save storage • First photo is main</span>
                  </>
                )}
              </button>
            </div>
            
            <input
              ref={photoInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>

          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Mystic Moonlight"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Tell collectors about this polish..."
            />
          </div>

          {/* Color Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color Description
            </label>
            <input
              type="text"
              value={colorDescription}
              onChange={(e) => setColorDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Deep purple with holographic shimmer and gold flakies"
            />
          </div>

          {/* Item Type — determines which detail-page format the brand
              catalog entry renders in and which URL route it uses on the
              brand page. Default 'polish' so existing catalog behavior is
              unchanged; brands who now sell plates/oils/etc. can pick the
              right type per product. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item Type
            </label>
            <select
              value={itemType}
              onChange={(e) => setItemType(e.target.value as import('../../types').ItemType)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="polish">Polish</option>
              <option value="plate">Plate</option>
              <option value="cuticle_care">Cuticle Care</option>
              <option value="embellishment">Embellishment</option>
              <option value="transfer">Transfer / Decal</option>
              <option value="powder">Powder</option>
              <option value="acrylic">Acrylic</option>
              <option value="tool">Tool</option>
            </select>
          </div>

          {/* Category & Collection Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Collection
              </label>
              <select
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">No collection</option>
                {collections.map(col => (
                  <option key={col.id} value={col.id}>{col.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {STATUS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value)}
                  className={`p-3 text-center rounded-lg border-2 transition-all ${
                    status === option.value
                      ? `${option.color} border-current`
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <span className="text-lg mr-1">{option.emoji}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Price & SKU Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Price (optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="12.00"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SKU (optional)
              </label>
              <input
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., MM-2024-001"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              Tags
            </label>
            
            {/* Existing tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-purple-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Add tag input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., holo, glitter, crelly"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Press Enter or click Add to add tags</p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
